(() => {
  class MacksCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-macks-track]');
      this.slides = Array.from(root.querySelectorAll('[data-macks-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-macks-dot]'));
      this.previous = root.querySelector('[data-macks-previous]');
      this.next = root.querySelector('[data-macks-next]');
      this.index = 0;
      this.timer = null;
      this.interval = Math.max(parseInt(root.dataset.interval || '5000', 10), 3000);
      this.autoplay = root.dataset.autoplay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.track || this.slides.length < 2) return;

      this.previous?.addEventListener('click', () => this.goTo(this.index - 1, true));
      this.next?.addEventListener('click', () => this.goTo(this.index + 1, true));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index, true)));
      this.root.addEventListener('mouseenter', () => this.stop());
      this.root.addEventListener('mouseleave', () => this.start());
      this.root.addEventListener('focusin', () => this.stop());
      this.root.addEventListener('focusout', () => this.start());
      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.goTo(this.index - 1, true);
        if (event.key === 'ArrowRight') this.goTo(this.index + 1, true);
      });

      let touchStart = null;
      this.root.addEventListener('touchstart', (event) => {
        touchStart = event.touches[0]?.clientX ?? null;
        this.stop();
      }, { passive: true });
      this.root.addEventListener('touchend', (event) => {
        if (touchStart !== null) {
          const touchEnd = event.changedTouches[0]?.clientX ?? touchStart;
          const delta = touchEnd - touchStart;
          if (Math.abs(delta) > 45) this.goTo(this.index + (delta < 0 ? 1 : -1), true);
        }
        touchStart = null;
        this.start();
      }, { passive: true });

      this.render();
      this.start();
    }

    goTo(index, restart = false) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.render();
      if (restart) {
        this.stop();
        this.start();
      }
    }

    render() {
      this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
    }

    start() {
      this.stop();
      if (!this.autoplay || this.reducedMotion || document.hidden) return;
      this.timer = window.setInterval(() => this.goTo(this.index + 1), this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }


  class MacksPasswordGate {
    constructor(root) {
      this.root = root;
      this.enabled = root.dataset.passwordEnabled === 'true';
      this.expectedHash = root.dataset.passwordHash || '';
      this.storageKey = root.dataset.storageKey || 'macks-page-access';
      this.accessHours = Math.max(Number.parseInt(root.dataset.accessHours || '24', 10), 1);
      this.designMode = root.dataset.designMode === 'true';

      this.gate = root.querySelector('[data-macks-password-gate]');
      this.content = root.querySelector('[data-macks-protected-content]');
      this.form = root.querySelector('[data-macks-password-form]');
      this.input = root.querySelector('[data-macks-password-input]');
      this.error = root.querySelector('[data-macks-password-error]');
      this.submit = root.querySelector('[data-macks-password-submit]');
      this.toggle = root.querySelector('[data-macks-password-toggle]');
      this.showLabel = root.querySelector('[data-macks-show-label]');
      this.hideLabel = root.querySelector('[data-macks-hide-label]');

      if (!this.content) return;

      this.addNoIndex();

      if (!this.enabled || this.designMode || this.hasValidAccess()) {
        this.unlock(false);
        return;
      }

      this.lock();
      this.bind();
    }

    addNoIndex() {
      if (!this.enabled) return;

      let robots = document.head.querySelector('meta[name="robots"][data-macks-noindex]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.name = 'robots';
        robots.content = 'noindex,nofollow,noarchive';
        robots.dataset.macksNoindex = 'true';
        document.head.appendChild(robots);
      }
    }

    bind() {
      this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
      this.toggle?.addEventListener('click', () => this.togglePasswordVisibility());
      this.input?.addEventListener('input', () => this.clearError());
    }

    lock() {
      this.gate?.removeAttribute('hidden');
      this.content.setAttribute('hidden', '');
      this.content.setAttribute('inert', '');
      this.content.setAttribute('aria-hidden', 'true');
      this.root.dataset.macksUnlocked = 'false';

      window.requestAnimationFrame(() => {
        this.input?.focus({ preventScroll: true });
      });
    }

    unlock(remember = true) {
      if (remember && this.enabled) {
        this.saveAccess();
      }

      this.gate?.setAttribute('hidden', '');
      this.content.removeAttribute('hidden');
      this.content.removeAttribute('inert');
      this.content.removeAttribute('aria-hidden');
      this.root.dataset.macksUnlocked = 'true';
      this.hydrateEmbeds();
      initializeCarousels(this.content);
    }

    hydrateEmbeds() {
      this.content.querySelectorAll('iframe[data-src]').forEach((iframe) => {
        if (!iframe.getAttribute('src')) {
          iframe.setAttribute('src', iframe.dataset.src);
        }
      });
    }

    async handleSubmit(event) {
      event.preventDefault();

      const password = this.input?.value || '';
      if (!password) {
        this.showError();
        return;
      }

      this.setBusy(true);

      try {
        const enteredHash = await this.hash(password);

        if (this.safeEqual(enteredHash, this.expectedHash)) {
          this.clearError();
          this.unlock(true);
          return;
        }

        this.showError();
        this.input?.select();
      } catch (error) {
        console.error('Mack’s password gate could not validate the password.', error);
        this.showError('Your browser could not validate the password. Please try another browser.');
      } finally {
        this.setBusy(false);
      }
    }

    async hash(value) {
      if (!window.crypto?.subtle) {
        throw new Error('Web Crypto API is unavailable.');
      }

      const bytes = new TextEncoder().encode(value);
      const digest = await window.crypto.subtle.digest('SHA-256', bytes);

      return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
    }

    safeEqual(left, right) {
      if (!left || !right || left.length !== right.length) return false;

      let result = 0;
      for (let index = 0; index < left.length; index += 1) {
        result |= left.charCodeAt(index) ^ right.charCodeAt(index);
      }

      return result === 0;
    }

    hasValidAccess() {
      try {
        const saved = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');

        return Boolean(
          saved &&
          saved.hash === this.expectedHash &&
          Number(saved.expiresAt) > Date.now()
        );
      } catch (_error) {
        return false;
      }
    }

    saveAccess() {
      try {
        window.localStorage.setItem(
          this.storageKey,
          JSON.stringify({
            hash: this.expectedHash,
            expiresAt: Date.now() + (this.accessHours * 60 * 60 * 1000)
          })
        );
      } catch (_error) {
        // The page still unlocks when local storage is unavailable.
      }
    }

    setBusy(busy) {
      if (this.submit) {
        this.submit.disabled = busy;
        this.submit.setAttribute('aria-busy', String(busy));
      }

      if (this.input) {
        this.input.disabled = busy;
      }
    }

    showError(message = '') {
      if (!this.error || !this.input) return;

      if (message) {
        this.error.textContent = message;
      }

      this.error.removeAttribute('hidden');
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
    }

    clearError() {
      this.error?.setAttribute('hidden', '');
      this.input?.removeAttribute('aria-invalid');
    }

    togglePasswordVisibility() {
      if (!this.input || !this.toggle) return;

      const showing = this.input.type === 'text';
      this.input.type = showing ? 'password' : 'text';
      this.toggle.setAttribute('aria-pressed', String(!showing));
      this.toggle.setAttribute('aria-label', showing ? 'Show password' : 'Hide password');

      if (this.showLabel && this.hideLabel) {
        this.showLabel.hidden = !showing;
        this.hideLabel.hidden = showing;
      }

      this.input.focus();
    }
  }

  const initializeCarousels = (scope = document) => {
    scope.querySelectorAll('[data-macks-carousel]').forEach((root) => {
      if (!root.closest('[data-macks-protected-content][hidden]') && !root.dataset.macksInitialized) {
        root.dataset.macksInitialized = 'true';
        new MacksCarousel(root);
      }
    });
  };


  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-macks-protected-page]').forEach((root) => {
      if (!root.dataset.macksGateInitialized) {
        root.dataset.macksGateInitialized = 'true';
        new MacksPasswordGate(root);
      }
    });

    scope.querySelectorAll('[data-macks-protected-page][data-macks-unlocked="true"]').forEach((root) => {
      initializeCarousels(root);
    });

    scope.querySelectorAll('[data-macks-protected-page]').forEach((root) => {
      const enabled = root.dataset.passwordEnabled === 'true';
      const designMode = root.dataset.designMode === 'true';

      if (!enabled || designMode) {
        initializeCarousels(root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
